/**
 * Public Chat Hook
 * 
 * Manages anonymous chat sessions for the public landing page.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type {
  PublicMessage,
  GetSessionResponse,
  QuickAction,
} from '../public-chat.types';

const API_BASE = '/api/v1/public-chat';
const SESSION_STORAGE_KEY = 'pai-public-session';

interface UsePublicChatReturn {
  sessionId: string | null;
  messages: PublicMessage[];
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  quickActions: QuickAction[];
  rateLimitRemaining: number;
  sendMessage: (content: string) => Promise<void>;
  clearError: () => void;
  initSession: () => Promise<void>;
}

export function usePublicChat(): UsePublicChatReturn {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<PublicMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quickActions, setQuickActions] = useState<QuickAction[]>([]);
  const [rateLimitRemaining, setRateLimitRemaining] = useState(30);
  const initRef = useRef(false);

  // Get stored session ID
  const getStoredSessionId = useCallback((): string | null => {
    try {
      return localStorage.getItem(SESSION_STORAGE_KEY);
    } catch {
      return null;
    }
  }, []);

  // Store session ID
  const storeSessionId = useCallback((id: string) => {
    try {
      localStorage.setItem(SESSION_STORAGE_KEY, id);
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Initialize session
  const initSession = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const storedId = getStoredSessionId();
      const locale = navigator.language.split('-')[0] || 'en';

      const response = await fetch(`${API_BASE}/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: storedId,
          locale,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to initialize session');
      }

      const data: GetSessionResponse = await response.json();

      setSessionId(data.session.sessionId);
      storeSessionId(data.session.sessionId);

      if (data.quickActions) {
        setQuickActions(data.quickActions);
      }

      // If there's a welcome message and we don't have messages yet
      if (data.welcome && messages.length === 0) {
        setMessages([data.welcome]);
      } else if (data.session.messageCount > 0) {
        // Load existing history
        const historyResponse = await fetch(`${API_BASE}/history/${data.session.sessionId}`);
        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          setMessages(historyData.messages || []);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to connect');
    } finally {
      setIsLoading(false);
    }
  }, [getStoredSessionId, storeSessionId, messages.length]);

  // Send a message with SSE streaming
  const sendMessage = useCallback(async (content: string) => {
    if (!sessionId || isSending || !content.trim()) return;

    setIsSending(true);
    setError(null);

    const trimmedContent = content.trim();
    const locale = navigator.language.split('-')[0] || 'en';

    // Optimistically add user message
    const tempUserMessage: PublicMessage = {
      id: uuidv4(),
      role: 'user',
      content: trimmedContent,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMessage]);

    // Create placeholder for streaming assistant message
    const tempAssistantMessage: PublicMessage = {
      id: uuidv4(),
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true,
    };
    setMessages((prev) => [...prev, tempAssistantMessage]);

    try {
      const response = await fetch(`${API_BASE}/message/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          message: trimmedContent,
          locale,
        }),
      });

      if (response.status === 429) {
        const rateLimitData = await response.json();
        setError(`Rate limit reached. Please try again in ${Math.ceil(rateLimitData.retryAfter / 60)} minutes.`);
        setRateLimitRemaining(0);
        // Remove both temp messages
        setMessages((prev) => prev.filter((m) => m.id !== tempUserMessage.id && m.id !== tempAssistantMessage.id));
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      // Read the SSE stream
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';
      let streamedContent = '';
      let actualUserMessageId = tempUserMessage.id;
      let actualAssistantMessageId = tempAssistantMessage.id;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));

              if (event.type === 'start') {
                // Update message IDs if provided
                if (event.userMessageId) actualUserMessageId = event.userMessageId;
                if (event.messageId) actualAssistantMessageId = event.messageId;
                if (event.rateLimit) {
                  setRateLimitRemaining(event.rateLimit.remaining);
                }
              } else if (event.type === 'chunk' && event.content) {
                streamedContent += event.content;
                // Update the streaming message (keep isStreaming: true)
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === tempAssistantMessage.id
                      ? { ...m, id: actualAssistantMessageId, content: streamedContent, isStreaming: true }
                      : m.id === tempUserMessage.id
                      ? { ...m, id: actualUserMessageId }
                      : m
                  )
                );
              } else if (event.type === 'done') {
                // Finalize the message (set isStreaming: false)
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === tempAssistantMessage.id || m.id === actualAssistantMessageId
                      ? {
                          ...m,
                          id: actualAssistantMessageId,
                          content: streamedContent,
                          metadata: event.cards ? { cards: event.cards } : undefined,
                          isStreaming: false,
                        }
                      : m
                  )
                );
              } else if (event.type === 'error') {
                setError(event.content || 'An error occurred');
              }
            } catch {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
      // Remove both temp messages on error
      setMessages((prev) => prev.filter((m) => m.id !== tempUserMessage.id && m.id !== tempAssistantMessage.id));
    } finally {
      setIsSending(false);
    }
  }, [sessionId, isSending]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Initialize on mount
  useEffect(() => {
    if (!initRef.current) {
      initRef.current = true;
      initSession();
    }
  }, [initSession]);

  return {
    sessionId,
    messages,
    isLoading,
    isSending,
    error,
    quickActions,
    rateLimitRemaining,
    sendMessage,
    clearError,
    initSession,
  };
}

export default usePublicChat;

